import { ref, computed, watch } from '@vue/composition-api'
import analytics from '@/@core/utils/analytics'
import {
  apiToastError,
  apiToastSuccess,
  apiToastWarning,
} from '@/@core/utils/toast'
import { getProperProfileData } from '@/common-utils'
import constants, {
  CART_EXPORT_MODAL_ID,
  MONTH_START_DATE,
  VUE_FLATPICKR_MONTH_CONFIG,
} from '@/constants'
import store from '@/store'
import { FETCH_PROFILE } from '@/store/modules/auth.module'
import { FETCH_BRAND_DETAIL } from '@/store/modules/brand.module'
import { GET_COUNTRIES } from '@/store/modules/common.module'
import { CREATE_ORDER } from '@/store/modules/order.module'
import {
  ADD_PRODUCT_TO_CART,
  CLEAR_CART,
  UPDATE_CART_ITEMS_COUNT,
  UPDATE_CART_EXPECTED_DELIVERY_DATE,
} from '@/store/modules/shop.module'
import { UPDATE_CART_ERRORS, LOAD_CARTS } from '@/store/modules/checkout-v2.module'
import { formatNumber, formatObject } from '@core/utils/utils'
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'
import { debounce } from 'lodash'

const { formatCurrency, formattedDate } = utils
const { KP_MONTH_FORMAT } = c

/**
 * Parses expectedDeliveryDate from the API into mm/yyyy for the month picker.
 * Handles dd/mm/yyyy (stored format) and ISO dates (backend may serialize as Date).
 */
export function parseDeliveryDate(raw) {
  if (!raw) return ''
  const str = String(raw)
  // dd/mm/yyyy → extract mm/yyyy
  const slashParts = str.split('/')
  if (slashParts.length === 3) {
    return `${slashParts[1]}/${slashParts[2]}`
  }
  // ISO date (e.g. 2024-12-01T00:00:00.000Z) or yyyy-mm-dd
  const d = new Date(str)
  if (!Number.isNaN(d.getTime())) {
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    const yyyy = d.getUTCFullYear()
    return `${mm}/${yyyy}`
  }
  return ''
}

/**
 * useCartTotal — shared business logic for cart total / checkout flow.
 *
 * @param {Object} props  - component props (isPopup, cartProducts, retailerId, brandNotes, modalId, getCartProductsByBrand)
 * @param {Object} cbs    - injection callbacks (framework-dependent)
 *   - onShowSubModal(id)       - show a sub-modal by id
 *   - onHideSubModal(id)       - hide a sub-modal by id
 *   - getCartProductsRef()     - returns the $refs.cartProducts instance
 *   - emit(event, ...args)     - component emit
 */
export function useCartTotal(props, cbs = {}) {
  const {
    onShowSubModal = () => {},
    onHideSubModal = () => {},
    getCartProductsRef = () => null,
    emit = () => {},
  } = cbs

  // ─── State ───────────────────────────────────────────────────────────────
  const totalMRP = ref(null)
  const totalUnits = ref(null)
  const total = ref(null)
  const currency = ref(null)
  const showTotal = ref(false)
  const profileData = ref(null)
  const addresses = ref([])
  const countryData = ref(null)
  const creditsUsed = ref(0)
  const remainingCredits = ref(0)
  const showCreditInput = ref(false)
  const discountInCredit = ref(0)
  const appliedCredits = ref(0)
  const acceptTerms = ref(false)
  const isTermsUnChecked = ref(false)
  const isDisabledPlaceOrder = ref(false)
  const isPlacingOrder = ref(false)
  const isEditNotes = ref(false)
  const notes = ref('')
  const isSavingNotes = ref(false)
  const isEditDeliveryWindow = ref(false)
  const expectedDeliveryDate = ref('')
  const isSavingDeliveryWindow = ref(false)

  // ─── Computed ─────────────────────────────────────────────────────────────
  const getMaxCreditEligible = computed(() => {
    const eligible = totalMRP.value - 1 > discountInCredit.value
      ? discountInCredit.value
      : Number(totalMRP.value) - 1
    return eligible
  })

  const isDirectRetailerNotSelected = computed(() => props.isPopup && !props.retailerId)

  const shouldAddNotes = computed(() => {
    const isBrand = store.getters.entityType === 'brand' || store.getters.isBrand
    return isBrand && props.isPopup && props.retailerId
  })

  const collectionId = computed(() => getCartProductsRef()?.collectionId)
  const collectionName = computed(() => getCartProductsRef()?.collectionName)

  // ─── Watchers ─────────────────────────────────────────────────────────────
  watch(creditsUsed, val => {
    total.value = totalMRP.value - val
    remainingCredits.value = discountInCredit.value - val
  })

  watch(acceptTerms, val => {
    if (!val) isTermsUnChecked.value = false
  })

  watch(notes, val => {
    if (props.isPopup) {
      emit('update-notes', {
        notes: notes.value,
        isEditNotes: isEditNotes.value,
      })
    }
    if (val) {
      debouncedTrackEnterNotes({
        inputText: val,
        collectionId: collectionId.value,
        retailerId: getRetailerId(),
        cartId: getCartId(),
        products: getCartOrPropsProducts(),
      })
    }
  })

  // ─── Initialize ──────────────────────────────────────────────────────────
  function initialize() {
    loadCountries()
    getProfileData()
    initPopupNotes()
    initDeliveryWindow()
  }

  function initPopupNotes() {
    if (!props.isPopup) return
    notes.value = props.brandNotes?.notes || ''
    isEditNotes.value = props.brandNotes?.isEditNotes || false
  }

  // Only the retailer's own cart carries a saved delivery window. The brand
  // popup orders props.cartProducts (not store.getters.getCart), so prefilling
  // from the store cart here would leak an unrelated window into the popup
  // order — keep it blank until the brand explicitly selects one.
  function initDeliveryWindow() {
    if (props.isPopup) return
    const cart = store.getters.getCart
    if (cart?.expectedDeliveryDate) {
      expectedDeliveryDate.value = parseDeliveryDate(cart.expectedDeliveryDate)
    }
  }

  // ─── Data loading ────────────────────────────────────────────────────────
  function loadCountries() {
    store
      .dispatch(GET_COUNTRIES)
      .then(res => {
        countryData.value = res?.data?.data
      })
      .catch(err => {
        apiToastError(err)
      })
  }

  async function getProfileData() {
    const profile = props.isPopup && props.retailerId
      ? await fetchBrandProfile()
      : await fetchUserProfile()

    profileData.value = getProperProfileData(profile)
    addresses.value = profile?.addresses || []
  }

  async function fetchBrandProfile() {
    const res = await store.dispatch(FETCH_BRAND_DETAIL, props.retailerId)
    return res.data.data
  }

  async function fetchUserProfile() {
    const res = await store.dispatch(FETCH_PROFILE)
    return res
  }

  // ─── Cart total updates ──────────────────────────────────────────────────
  function updateCartTotal({ totalMRP: mrp, currency: curr }) {
    totalMRP.value = Number(mrp).toFixed(2)
    total.value = Number(mrp).toFixed(2) - Number(creditsUsed.value).toFixed(2)
    if (curr) currency.value = curr
  }

  function updateCartTotalUnits(units) {
    totalUnits.value = units
  }

  function updateListView(hasProducts) {
    discountInCredit.value = getCartProductsRef()?.discountInCredit || 0
    remainingCredits.value = discountInCredit.value - appliedCredits.value
    creditsUsed.value = appliedCredits.value
    showTotal.value = !!hasProducts
  }

  // ─── Notes ───────────────────────────────────────────────────────────────
  function handleNotesEdit() {
    isEditNotes.value = true
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLICKS_NOTES_ICON,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_NOTES_ICON,
      { products: getCartOrPropsProducts() },
    )
  }

  function getNotesTrackData() {
    return {
      cartId: getCartId(),
      retailerId: getRetailerId(),
      inputText: notes.value,
    }
  }

  async function handleSaveNotes(payload) {
    try {
      const res = await store.dispatch(ADD_PRODUCT_TO_CART, { payload })
      apiToastSuccess(res.data.message || 'Notes updated successfully')
      analytics.track(
        constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SAVE_NOTE_SUCCESS,
        getNotesTrackData(),
      )
    } catch (err) {
      analytics.track(
        constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SAVE_NOTE_FAILED,
        { ...getNotesTrackData(), error: err?.message || 'Something went wrong' },
      )
    } finally {
      isSavingNotes.value = false
      isEditNotes.value = false
    }
  }

  async function onUpdateNotes() {
    analytics.track(
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_SAVE_BUTTON_FOR_NOTES,
      getNotesTrackData(),
    )
    if (!notes.value) {
      isEditNotes.value = false
      return
    }
    const payload = { notes: notes.value }
    if (props.isPopup && props.retailerId) {
      payload.retailerId = props.retailerId
    }
    isSavingNotes.value = true
    await handleSaveNotes(payload)
  }

  const debouncedTrackEnterNotes = debounce(({
    inputText, collectionId: cId, retailerId, products, cartId,
  }) => {
    const isBrand = shouldAddNotes.value
    const trackName = getBrandOrRetailerTrackName(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_ENTERS_TEXT_IN_NOTES_INPUT,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_ENTERS_TEXT_IN_NOTES_INPUT,
    )
    analytics.track(trackName, {
      collectionId: isBrand ? cId : undefined,
      cartId: isBrand ? undefined : cartId,
      retailerId,
      products,
      inputText,
    })
  }, 500)

  // ─── Delivery Window ─────────────────────────────────────────────────────
  function isValidDeliveryDate() {
    if (!expectedDeliveryDate.value) return false
    const [month, year] = expectedDeliveryDate.value.split('/')
    const selectedDate = new Date(year, month - 1, 1)
    const now = new Date()
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    return selectedDate >= currentMonth
  }

  function getDeliveryWindowTrackData() {
    return {
      cartId: getCartId(),
      formattedDate: `${MONTH_START_DATE}${expectedDeliveryDate.value}`,
      retailerId: getRetailerId(),
    }
  }

  /**
   * The requested delivery window to attach to the place-order payload, or null.
   * Only the brand popup carries it on the order (it has no persistent cart);
   * isValidDeliveryDate() already returns false for an empty/past date.
   */
  function getOrderDeliveryWindow() {
    if (!props.isPopup || !isValidDeliveryDate()) return null
    return `${MONTH_START_DATE}${expectedDeliveryDate.value}`
  }

  function buildDeliveryWindowAnalyticsPayload(trackData) {
    return {
      cartId: trackData.cartId,
      retailerId: trackData.retailerId,
      expectedDeliveryDate: trackData.formattedDate,
    }
  }

  async function onUpdateDeliveryWindow() {
    if (!isValidDeliveryDate()) {
      apiToastError('Please select current or future month')
      return
    }
    const trackData = getDeliveryWindowTrackData()
    const analyticsPayload = buildDeliveryWindowAnalyticsPayload(trackData)
    analytics.track(
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_SAVE_DELIVERY_WINDOW_BTN,
      analyticsPayload,
    )
    // In the brand popup there is no persisted cart to update independently
    // (cartId is empty). The chosen window is instead sent with the place-order
    // request (see buildOrderData), so just confirm the local selection here.
    if (props.isPopup) {
      isEditDeliveryWindow.value = false
      return
    }
    isSavingDeliveryWindow.value = true
    try {
      const res = await store.dispatch(UPDATE_CART_EXPECTED_DELIVERY_DATE, {
        payload: { cartId: trackData.cartId, expectedDeliveryDate: trackData.formattedDate },
      })
      isSavingDeliveryWindow.value = false
      isEditDeliveryWindow.value = false
      apiToastSuccess(res.data.message)
      analytics.track(
        constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SAVE_DELIVERY_WINDOW_SUCCESS,
        analyticsPayload,
      )
    } catch (err) {
      isSavingDeliveryWindow.value = false
      apiToastError(err)
      analytics.track(
        constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_SAVE_DELIVERY_WINDOW_FAILED,
        { ...analyticsPayload, error: err?.message || 'Something went wrong' },
      )
    }
  }

  // ─── Terms ───────────────────────────────────────────────────────────────
  function validateTerms() {
    if (!acceptTerms.value) {
      isTermsUnChecked.value = true
      updateIsPlacingOrder(false)
      return false
    }
    return true
  }

  function updateTerms() {
    acceptTerms.value = true
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLICKS_TERMS_AND_CONDITIONS_MODAL_ACCEPT_BUTTON,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_TERMS_AND_CONDITIONS_MODAL_ACCEPT_BUTTON,
      { products: getRefProducts() },
    )
  }

  function handleTermsAndConditionLink(open = false) {
    if (open) onShowSubModal('cart-terms')
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLICKS_TERMS_AND_CONDITIONS_LINK,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_TERMS_AND_CONDITIONS_LINK,
      { products: getRefProducts() },
    )
  }

  function handleTermsModalClose() {
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLOSES_TERMS_AND_CONDITIONS_MODAL,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLOSES_TERMS_AND_CONDITIONS_MODAL,
      { products: getRefProducts() },
    )
  }

  // ─── Order placement ──────────────────────────────────────────────────────
  function buildOrderData() {
    const data = {}
    if (props.isPopup && props.retailerId) {
      data.retailerId = props.retailerId
    }
    // Brand popup has no persisted cart, so the requested delivery window is
    // sent with the order instead of via the independent cart endpoint.
    const orderDeliveryWindow = getOrderDeliveryWindow()
    if (orderDeliveryWindow) {
      data.expectedDeliveryDate = orderDeliveryWindow
    }
    if (appliedCredits.value > 0) {
      data.discountInCredit = Number(appliedCredits.value)
    }
    return data
  }

  function updateIsDisablePlaceOrder(disable = false) {
    isDisabledPlaceOrder.value = disable
  }

  function updateIsPlacingOrder(isPlacing = false) {
    updateIsDisablePlaceOrder(isPlacing)
    isPlacingOrder.value = isPlacing
  }

  async function saveCart(shouldSaveCart = false) {
    const notesPayload = {}
    if (shouldAddNotes.value) {
      notesPayload.notes = notes.value
      notesPayload.retailerId = props.retailerId
    }
    return await getCartProductsRef()?.saveCartProducts(notesPayload, shouldSaveCart)
  }

  function getZeroValueProducts() {
    return getRefProducts().filter(
      cartProduct => !cartProduct.isCarton && !hasValueInItems(cartProduct.items),
    )
  }

  function hasValueInItems(items) {
    return items?.some(item => item.value)
  }

  function trackPlaceOrder() {
    const zeroValueProducts = getZeroValueProducts()
    const noOfProductsPlacedToOrder = (getCartProductsRef()?.products?.length || 0) - zeroValueProducts.length

    analytics.track(
      constants.TRACKS.ACTIONS.RETAILER_PLACES_ORDER,
      formatObject({
        collectionName: collectionName.value,
        noOfProductsPlacedToOrder,
        noOfProductsLeft: zeroValueProducts.length,
      }),
    )
  }

  function trackOrderSuccess() {
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_PLACE_ORDER_SUCCESS,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_PLACE_ORDER_SUCCESS,
      { products: getRefProducts() },
    )
  }

  function trackOrderError(err) {
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_PLACE_ORDER_FAILED,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_PLACE_ORDER_FAILED,
      { products: getCartOrPropsProducts(), error: err?.message },
    )
  }

  function handleOrderSuccess(res) {
    trackOrderSuccess()
    store.commit(UPDATE_CART_ITEMS_COUNT, 0)
    apiToastSuccess(res.data.message)
    const { orderId, orderSeqId } = res.data.data

    if (cbs.onOrderSuccess) {
      cbs.onOrderSuccess(orderId, orderSeqId)
    }

    updateIsPlacingOrder(false)
  }

  function handleOrderError(err, shouldClearCart) {
    trackOrderError(err)
    updateIsPlacingOrder(false)

    if (props.isPopup && shouldClearCart) {
      clearCart(props.retailerId)
    }

    if (err?.response?.data?.data?.shouldDisplayPopUp) {
      onShowSubModal('modal-order-fail')
    } else {
      store.commit(UPDATE_CART_ERRORS)
    }
  }

  async function placeOrder(shouldClearCart = false) {
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLICKS_ON_PLACE_ORDER_BUTTON,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_ON_PLACE_ORDER_BUTTON,
      { products: getCartOrPropsProducts() },
    )

    if (shouldClearCart) {
      await clearCart(props.retailerId)
    }

    updateIsPlacingOrder(true)

    if (!validateTerms()) return

    try {
      await saveCart()
      trackPlaceOrder()

      const data = buildOrderData()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const res = await store.dispatch(CREATE_ORDER, data)

      handleOrderSuccess(res)
    } catch (err) {
      handleOrderError(err, shouldClearCart)
    }
  }

  // ─── Clear / export cart ──────────────────────────────────────────────────
  async function clearCart(retailerId = '') {
    const payload = {}
    if (retailerId) payload.retailerId = props.retailerId

    analytics.track(
      constants.TRACKS.ACTIONS.RETAILER_CLEARS_CART,
      formatObject({
        noOfProductsInCart: store.state.shop.cartItemsCount,
        collectionName: collectionName.value,
      }),
    )

    await store
      .dispatch(CLEAR_CART, { payload })
      .then(res => {
        if (!props.isPopup) {
          store.commit(UPDATE_CART_ITEMS_COUNT, 0)
          apiToastSuccess(res.data.message)
          store.dispatch(LOAD_CARTS)
        }
      })
      .catch(() => { })

    onHideSubModal('clear-cart')
    onHideSubModal('clear-retailer-cart')
  }

  async function onClickExportCart() {
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLICKS_ON_EXPORT_BUTTON,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_ON_EXPORT_BUTTON,
      { products: getRefProducts() },
    )

    if (isDirectRetailerNotSelected.value) {
      apiToastWarning('Please select a retailer.')
      return
    }

    try {
      await saveCart(true)
    } catch (e) {
      return
    }

    onShowSubModal(CART_EXPORT_MODAL_ID)
  }

  // ─── Credits ─────────────────────────────────────────────────────────────
  function applyCredits() {
    if (creditsUsed.value > 0) {
      apiToastSuccess('Your credit points were applied to the order successfully.')
    }
    appliedCredits.value = creditsUsed.value
    showCreditInput.value = false
  }

  // ─── Import / export events ───────────────────────────────────────────────
  async function onCartImportSuccess() {
    if (props.isPopup) {
      // Tell V2CartProducts to NOT skip re-init on the next cartProducts change.
      // The watcher normally skips when product IDs are the same (to preserve user
      // edits during retailer switch), but after import the same products have new
      // quantities that must be picked up. Setting the flag BEFORE the refetch
      // guarantees it's in place when the watcher fires.
      getCartProductsRef()?.scheduleReinitialize?.()
      // Fetch from the cart endpoint so imported quantities are included.
      // The catalog endpoint (GET_PRODUCTS_BY_IDS) returns products without
      // cart quantities, which is why imports appeared to lose quantity data.
      await props.getCartProductsByBrand(props.retailerId, { fetchFromCart: true })
    } else {
      await store.dispatch(LOAD_CARTS)
    }
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_IMPORT_FILE_SUCCESS,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_IMPORT_FILE_SUCCESS,
      { products: getCartOrPropsProducts() },
    )
  }

  async function onCartExportSuccess() {
    if (props.isPopup) {
      await clearCart(props.retailerId)
    }
  }

  function removeProductSelectedFromBrand(product) {
    emit('remove-product-selected-from-brand', product)
  }

  function removeMultipleProductsFromBrand(productIds) {
    emit('remove-multiple-products', productIds)
  }

  // ─── Analytics helpers ────────────────────────────────────────────────────

  /** Resolves the current cart ID from the Vuex store. */
  function getCartId() {
    return store.getters.getCart?._id ?? ''
  }

  /** Resolves the retailer ID from props or the Vuex cart. */
  function getRetailerId() {
    return props.retailerId || store.getters.getCart?.userId || ''
  }

  function getCommonDataForAnalytics() {
    const isBrand = shouldAddNotes.value
    return {
      cartId: isBrand ? undefined : getCartId(),
      collectionId: isBrand ? collectionId.value : undefined,
      retailerId: getRetailerId(),
    }
  }

  /**
   * Returns the products list from the cart-products ref component.
   */
  function getRefProducts() {
    return getCartProductsRef()?.products || []
  }

  /**
   * Returns products from props if available, otherwise falls back to the Vuex cart.
   */
  function getCartOrPropsProducts() {
    return props.cartProducts.length ? props.cartProducts : store.getters.getCart.products
  }

  /**
   * Selects the brand or retailer analytics track name based on entity type.
   */
  function getBrandOrRetailerTrackName(brandTrack, retailerTrack) {
    return shouldAddNotes.value ? brandTrack : retailerTrack
  }

  /**
   * Unified analytics tracker: picks brand/retailer track name, merges common data,
   * and fires the analytics event.
   */
  function trackBrandOrRetailer(brandTrack, retailerTrack, extraData = {}) {
    const trackName = getBrandOrRetailerTrackName(brandTrack, retailerTrack)
    analytics.track(trackName, {
      ...getCommonDataForAnalytics(),
      ...extraData,
    })
  }

  function trackTermsAndConditionsCheck(isChecked) {
    trackBrandOrRetailer(
      constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLICKS_TERMS_AND_CONDITIONS_CHECKBOX,
      constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_TERMS_AND_CONDITIONS_CHECKBOX,
      { products: getRefProducts(), isChecked },
    )
  }

  function trackImportCartEvents(eventName) {
    const brandTrack = eventName === 'close'
      ? constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLOSES_IMPORT_CART_MODAL
      : constants.TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_CLICKS_ON_IMPORT_BUTTON
    const retailerTrack = eventName === 'close'
      ? constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLOSES_IMPORT_CART_MODAL
      : constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.RETAILER_CLICKS_ON_IMPORT_BUTTON
    trackBrandOrRetailer(brandTrack, retailerTrack, { products: getRefProducts() })
  }

  return {
    // state
    totalMRP,
    totalUnits,
    total,
    currency,
    showTotal,
    profileData,
    addresses,
    countryData,
    creditsUsed,
    remainingCredits,
    showCreditInput,
    discountInCredit,
    appliedCredits,
    acceptTerms,
    isTermsUnChecked,
    isDisabledPlaceOrder,
    isPlacingOrder,
    isEditNotes,
    notes,
    isSavingNotes,
    isEditDeliveryWindow,
    expectedDeliveryDate,
    isSavingDeliveryWindow,
    // computed
    getMaxCreditEligible,
    isDirectRetailerNotSelected,
    shouldAddNotes,
    collectionId,
    collectionName,
    // utils
    formatNumber,
    formatCurrency,
    formattedDate,
    KP_MONTH_FORMAT,
    VUE_FLATPICKR_MONTH_CONFIG,
    // methods
    initialize,
    loadCountries,
    getProfileData,
    updateCartTotal,
    updateCartTotalUnits,
    updateListView,
    handleNotesEdit,
    onUpdateNotes,
    onUpdateDeliveryWindow,
    validateTerms,
    updateTerms,
    handleTermsAndConditionLink,
    handleTermsModalClose,
    buildOrderData,
    placeOrder,
    clearCart,
    onClickExportCart,
    applyCredits,
    onCartImportSuccess,
    onCartExportSuccess,
    removeProductSelectedFromBrand,
    removeMultipleProductsFromBrand,
    trackTermsAndConditionsCheck,
    trackImportCartEvents,
    updateIsPlacingOrder,
    saveCart,
  }
}
